import { SyntheticEvent, useState } from 'react'

import * as R from 'core/helpers/remeda'

export const validators = {
  required: (value: unknown) =>
    (!R.isNil(value) &&
      value !== '' &&
      (!R.isArray(value) || !R.isEmpty(value))) ||
    'This field is required',
}

export type Field<V, K extends string | number | symbol = string> = {
  value: V
  /** The input name. Must be unique in the containing form. */
  name?: K
  onChange?: (value: V) => void
  /** Disables all interactivity while true. */
  disabled?: boolean
  errorMessage?: string
  onBlur?: (e?: any) => void
}

type FormState<T> = {
  values: T
  defaultValues: T
  isDirty: boolean
  isSubmitting: boolean
  isValid: boolean
}

type Rule<T, V> =
  | boolean
  | Array<(value: V, formState: FormState<T>) => boolean | string>
  | ((value: V, formState: FormState<T>) => boolean | string)

const validate = <T, V>(rule: Rule<T, V>, value: V, formState: FormState<T>) =>
  !rule
    ? true
    : R.isArray(rule)
    ? R.pipe(
        rule,
        R.map((fn) => fn(value, formState)),
        R.find((res) => res !== true),
      ) ?? true
    : R.isFunction(rule)
    ? rule(value, formState)
    : rule

// TODO sc-55528
const useForm = <T extends object>(
  defaultValues: T,
  options?: {
    onSubmit?: (values: T) => unknown
    disabled?: boolean
    rules?: { [K in keyof T]?: Rule<T, T[K]> }
    validationType?: 'onBlur' | 'onChange' | null
  },
) => {
  const { validationType = 'onChange' } = options ?? {}
  const [isSubmitting, setSubmitting] = useState(false)

  const [values, setValues] = useState<Partial<T>>({})
  const [onBlurValidationResult, setOnBlurValidationResult] = useState<{
    [K in keyof T]?: string | boolean
  }>({})

  const currentValues: T = {
    ...defaultValues,
    ...values,
  }

  const formState = {
    values: currentValues,
    defaultValues,
    isDirty: !R.isEmpty(values),
    isSubmitting,
    isValid: false,
  }

  // @ts-expect-error
  const defaultValidationResult = R.mapValues(currentValues, (value, name) => {
    // TODO sc-53808
    const rule = options?.rules?.[name as any]

    return rule ? validate(rule, value as T[keyof T], formState) : true
  }) as { [K in keyof T]?: string | boolean }

  const validationResult = {
    ...defaultValidationResult,
    ...onBlurValidationResult,
  }

  return {
    fields: R.mapValues(currentValues as any, (value, name) => {
      const fieldName = name as keyof T
      return {
        value,
        name,
        disabled: options?.disabled || isSubmitting,
        onChange: (newValue: typeof value) => {
          setValues((currentPartialValues) => {
            const nextValues: T = {
              ...defaultValues,
              ...currentPartialValues,
              [name]: newValue,
            }
            const nextFormState: FormState<T> = {
              ...formState, // isSubmitting, defaultValues
              values: nextValues,
              isDirty: true, // it must be dirty if onChange is called
            }

            const rule = options?.rules?.[fieldName]
            const res = rule ? validate(rule, newValue, nextFormState) : true

            const otherTouchedFieldsResult = R.pipe(
              Object.keys(onBlurValidationResult), // Use the closure version of onBlurValidationResult, this is fine
              R.filter((key) => key !== name),
              // @ts-expect-error
              R.map((key) => {
                const fieldName = key as keyof T
                const fieldRule = options?.rules?.[fieldName]
                const fieldValue = nextValues[fieldName]
                return [
                  fieldName,
                  fieldRule
                    ? validate(fieldRule, fieldValue, nextFormState)
                    : true,
                ] as const
              }),
              R.fromPairs,
            )

            setOnBlurValidationResult((current) => ({
              ...current,
              ...otherTouchedFieldsResult,
              [name]: res,
            }))

            return {
              ...currentPartialValues,
              [name]: newValue,
            }
          })
        },
        errorMessage:
          validationType === 'onBlur' || validationType === 'onChange'
            ? R.isString(onBlurValidationResult[fieldName])
              ? onBlurValidationResult[fieldName]
              : undefined
            : R.isString(defaultValidationResult[fieldName])
            ? defaultValidationResult[fieldName]
            : undefined,
        onBlur: () => {
          if (validationType === 'onBlur') {
            const rule = options?.rules?.[fieldName]
            const res = rule ? validate(rule, value, formState) : true

            setOnBlurValidationResult((current) => ({
              ...current,
              [name]: res,
            }))
          }
        },
      }
    }) as unknown as { [K in keyof T]: Field<T[K], K> },
    formState: {
      ...formState,
      isValid:
        validationType === 'onBlur' || validationType === 'onChange'
          ? Object.values(validationResult).every((res) => res === true) &&
            !options?.disabled
          : Object.values(defaultValidationResult).every(
              (res) => res === true,
            ) && !options?.disabled,
    },
    setValues,
    clear: () => setValues({}),
    handleSubmit: (e?: Event | SyntheticEvent) => {
      e?.preventDefault()
      e?.stopPropagation()

      const res = options?.onSubmit?.(currentValues)

      if (res instanceof Promise) {
        setSubmitting(true)
        res
          .finally(() => setSubmitting(false))
          .catch((e) => console.error('Error in form submit', e))

        return res
      }

      return undefined
    },
  }
}

export default useForm
